import { formatPrice } from '../lib/price';
import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { CalendarBlank, Clock, Medal, Users, MapPin, ArrowRight, LockKey, LockKeyOpen } from '@phosphor-icons/react';
import { useAuth } from '../App';

const PublicMatch = () => {
  const { matchId } = useParams();
  const { user, language } = useAuth();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/match/${matchId}`;
        const { data } = await axios.get(url);
        setMatch(data);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Match not found');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [matchId]);

  // Logged-in users go straight to the live Open Matches page (with this match in context)
  if (user) {
    return <Navigate to={`/matches?match=${matchId}`} replace />;
  }

  const L = (en, de) => (language === 'de' ? de : en);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500" data-testid="public-match-loading">
        <div className="animate-pulse">{L('Loading match…', 'Lade Spiel…')}</div>
      </div>
    );
  }

  if (error || !match) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-6" data-testid="public-match-error">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">{L('Match not available', 'Spiel nicht verfügbar')}</h1>
        <p className="text-slate-500 mb-6">{L('It may have been cancelled or filled up.', 'Es wurde möglicherweise abgesagt oder ist voll.')}</p>
        <Link to="/" className="px-5 py-2.5 rounded-lg bg-emerald-500 text-white font-semibold hover:bg-emerald-600">
          {L('Browse open matches', 'Offene Spiele ansehen')}
        </Link>
      </div>
    );
  }

  const dateObj = new Date(match.date);
  const dateLocale = language === 'de' ? 'de-CH' : 'en-GB';
  const formattedDate = dateObj.toLocaleDateString(dateLocale, { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
  const courtReserved = !!match.court_reserved;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-slate-50 flex items-center justify-center px-4 py-10" data-testid="public-match-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-200/60 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white px-6 py-5">
          <div className="text-xs font-semibold uppercase tracking-wide opacity-80">{L('You\'re invited to', 'Du bist eingeladen zu')}</div>
          <h1 className="text-2xl font-extrabold mt-1">{L('an Open Match', 'einem offenen Spiel')}</h1>
          <p className="text-sm opacity-90 mt-1">Padelarena Wädenswil</p>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          <div className="flex items-center gap-3 text-slate-700">
            <CalendarBlank size={18} weight="duotone" className="text-emerald-500" />
            <span className="font-medium">{formattedDate}</span>
          </div>
          <div className="flex items-center gap-3 text-slate-700">
            <Clock size={18} weight="duotone" className="text-emerald-500" />
            <span className="font-medium">{match.start_time} – {match.end_time} ({match.duration_minutes} min)</span>
          </div>
          <div className="flex items-center gap-3 text-slate-700">
            <MapPin size={18} weight="duotone" className="text-emerald-500" />
            <span className="font-medium">{match.court_name}</span>
          </div>
          <div className="flex items-center gap-3 text-slate-700">
            <Medal size={18} weight="duotone" className="text-emerald-500" />
            <span className="font-medium">{L('Level', 'Level')} {match.min_level} – {match.max_level}</span>
          </div>
          <div className="flex items-center gap-3 text-slate-700">
            <Users size={18} weight="duotone" className="text-emerald-500" />
            <span className="font-medium">
              {match.players_joined}/{match.max_players} {L('players', 'Spieler')}
              {match.spots_available > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs font-bold uppercase rounded-md bg-emerald-50 text-emerald-700">
                  {match.spots_available} {L('open', 'offen')}
                </span>
              )}
            </span>
          </div>

          <div className={`mt-3 px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-2 border ${
            courtReserved
              ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
              : 'bg-amber-50 text-amber-700 border-amber-100'
          }`}>
            {courtReserved
              ? <><LockKey size={14} weight="fill" /> {L('Court reserved', 'Platz reserviert')}</>
              : <><LockKeyOpen size={14} /> {L('Awaiting 2nd player — court not yet locked', 'Wartet auf 2. Spieler — Platz noch nicht gesperrt')}</>}
          </div>

          <div className="pt-3 border-t border-slate-100">
            <div className="text-xs text-slate-500 mb-1">{L('Cost per player', 'Pro Spieler')}</div>
            <div className="text-3xl font-extrabold text-emerald-600">{formatPrice(match.cost_per_player)} CHF</div>
          </div>
        </div>

        {/* CTA */}
        <div className="px-6 pb-6">
          <Link
            to={`/login?next=${encodeURIComponent(`/matches?match=${match.id}`)}`}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-500 text-white font-bold hover:bg-emerald-600 transition-colors"
            data-testid="public-match-join-cta"
          >
            {L('Log in to join', 'Anmelden zum Mitspielen')}
            <ArrowRight size={18} weight="bold" />
          </Link>
          <p className="mt-3 text-center text-xs text-slate-500">
            {L('New here?', 'Neu hier?')}{' '}
            <Link to={`/register?next=${encodeURIComponent(`/matches?match=${match.id}`)}`} className="text-emerald-600 font-semibold hover:underline">
              {L('Create a free account', 'Kostenlos registrieren')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicMatch;
